import { computed, Injectable } from '@angular/core';
import { ALREADY_SPINED_SLOT_STR, BasePromo, EventType } from '../base-promo';
import { GameListConfig } from '../../../core/shared/components/game-list/game-list.component';
import { from, Observable, of } from 'rxjs';
import { ITournament } from '../../tournaments/tournaments.interface';
import { useStore } from 'ngx-unificator/store';
import { GlobalStoreName } from '../../../core/store';
import { filter, switchMap, tap } from 'rxjs/operators';
import { GameCategory } from '../../../core/services/games/game-category';
import { HalloweenPageResponse, HalloweenStoreEntity } from '../../../core/store/halloween.store';

const BONUS_IDS: string[] = ['promo_spins_1_sp25', 'promo_spins_2_sp25', 'promo_spins_3_sp25'];

export enum HalloweenAction {
  GO_TO_DEPOSIT = 'deposit',
  TOURNAMENTS = 'tournaments',
  PROMO_AUTH = 'promo-auth',
  GET_BONUS = 'get-bonus',
  GO_TO_BONUSES = 'bonuses',
  HUNT = 'hunt',
  HUNT_DEPOSIT = 'hunt-deposit',
}

@Injectable({
  providedIn: 'root',
})
export class HalloweenService extends BasePromo {
  /**
   * Event name
   */
  public eventName = 'halloween';

  /**
   * Game list config
   */
  public gamesListConfig: GameListConfig = {
    filter: {
      category: GameCategory.HALLOWEEN_GAMES,
      limit: 5,
    },
    link: '/games/halloween-games',
    title: '',
    hideTitle: true,
  };

  /**
   * Action rotes config
   */
  public actionRoutesConfig = {
    [HalloweenAction.GET_BONUS]: {
      key: null,
      url: '/account',
      authUrl: ['account'],
    },
    [HalloweenAction.GO_TO_BONUSES]: {
      key: null,
      url: '/bonuses',
      authUrl: ['bonuses'],
    },
    [HalloweenAction.TOURNAMENTS]: {
      key: 'promo-tournaments',
      url: '/tournaments',
      authUrl: ['tournaments'],
    },
    [HalloweenAction.GO_TO_DEPOSIT]: {
      key: 'promo-deposit',
      url: '/account/deposit',
      authUrl: ['account', 'deposit'],
    },
    [HalloweenAction.PROMO_AUTH]: {
      key: 'promo-auth',
      url: '/promo/halloween',
      authUrl: null,
    },
    [HalloweenAction.HUNT]: {
      key: null,
      url: '/missions',
      authUrl: null,
    },
    [HalloweenAction.HUNT_DEPOSIT]: {
      key: 'promo-deposit',
      url: '/account/deposit',
      authUrl: ['account', 'deposit'],
    },
  };

  /**
   * Tournaments list
   */
  public tournaments$: Observable<ITournament[]> = this.getTournaments$('halloween');

  /**
   * Halloween promo store
   */
  public halloweenStore = useStore<HalloweenStoreEntity>(GlobalStoreName.HALLOWEEN_STORE);

  /**
   * Halloween promo getters
   */
  public store = computed(() => this.halloweenStore.storeSignal());
  public slots = computed(() => this.halloweenStore.storeSignal().slots);

  constructor() {
    super();
  }

  /**
   * Init promotion and reduce store items
   */
  public initPromo$() {
    this.patchStore({ isLoadedPromo: false });
    this.user.auth$.pipe(
      switchMap(() => this.fetchPromoData$()),
      filter(data => !!data[0]),
      tap(([promo, lootboxes, freespins, bonuses]) => {
        this._updatePageStore(promo);
        this.updateCurrentBonus(lootboxes, freespins, bonuses, BONUS_IDS, this.halloweenStore);
        this.updateParticipateInPromo(lootboxes, BONUS_IDS, this.halloweenStore);
        this.patchStore({ isLoadedPromo: true });
        this.initHuntEvent(this.store().page.HuntModal);
      }),
      filter(() => this.platform.isBrowser),
      switchMap(() => from(this.initWelcomePromoModal())),
    ).subscribe();
  }

  /**
   * Activate bonus by type of bonus
   */
  public onActivateBonus$(): Observable<any> {
    const { promoIssuedLootbox, welcomeLootbox, fs, cash } = this.store().currentBonus || {};

    const cashPrize = cash ? `${cash.amount} ${cash.currency}` : undefined;

    if (this.store().isUserCanPartiallyParticipateInPromo) {
      if (welcomeLootbox) {
        return this.lootboxService.activateLootBox(welcomeLootbox.id).pipe(
          switchMap(response => {
            const fs_count = this.bonuses.resolveBonusAttributes(response?.bonuses[0].attributes)?.freespins_count;
            this._updateCurrentPrize(fs_count, cashPrize ?? undefined);
            const currentFsBonus = this.bonuses.mapBonusTitle(response?.bonuses[0]?.title);
            return this.bonuses.freeSpinsList().pipe(
              switchMap(list => {
                const foundFs = list.find(e => e.title === currentFsBonus);
                return this.bonuses.activateFreeSpins(foundFs?.id);
              }),
            );
          }),
        );
      } else if (fs) {
        return this.bonuses.activateFreeSpins(fs?.id).pipe(
          tap(() => {
            this._updateCurrentPrize(fs?.freespins_total, cashPrize ?? undefined);
          }),
        );
      }
    }

    if (promoIssuedLootbox) {
      return this.lootboxService.activateLootBox(promoIssuedLootbox?.id).pipe(
        switchMap(response => {
          const fs_count = this.bonuses.resolveBonusAttributes(response?.bonuses[0].attributes)?.freespins_count;
          this._updateCurrentPrize(fs_count);
          const currentFsBonus = this.bonuses.mapBonusTitle(response?.bonuses[0]?.title);
          this.patchStore({ showTryTomorrowOnDestroy: true });
          return this.bonuses.freeSpinsList().pipe(
            switchMap(list => {
              const foundFs = list.find(e => e.title === currentFsBonus);
              return this.bonuses.activateFreeSpins(foundFs?.id);
            }),
          );
        }),
      );
    }
  }

  /**
   * Update current prize store
   * @param fs_count
   * @param cash
   * @private
   */
  private _updateCurrentPrize(fs_count: number, cash?: string) {
    this.patchStore({
      currentPrize: {
        fs_count,
        cash,
      },
    });
    this.cookie.set(ALREADY_SPINED_SLOT_STR, '1');
  }

  /**
   * Update page store
   * @param promo
   * @private
   */
  private _updatePageStore(promo: HalloweenPageResponse): any {
    if (this.isPromoError(promo)) return;

    if (promo[0].isDisabledPromo) {
      this.patchStore({ page: promo[0] });
      return;
    }

    const prizes = [
      ...Object.values(promo[0]?.Prizes).map((prize: any) => ({
        ...prize,
        fs: Number(prize.fs),
        image: `/assets/img/promo/halloween/prizes/${Number(prize.image)}.png`,
      })),
    ];

    const page = {
      ...promo?.map(promoItem => ({
        ...promoItem,
        HowItWorksSteps: [...Object.values(promoItem?.HowItWorksSteps)],
        SocialList: [...Object.values(promoItem?.SocialList)],
        unpublishAt: this.time.toLocalDate(promoItem?.unpublishAt),
      }))[0],
    };

    const slots = {
      prizes,
      initialPrizes: prizes.slice(0, 3),
    };

    this.patchStore({ page, slots });
  }

  public patchStore(patch: Partial<HalloweenStoreEntity>) {
    this.halloweenStore.storeSignal.update(store => ({ ...store, ...patch }));
  }

  /**
   * Update final prize
   * @param fsCount
   */
  public updateLottieSlotPrize(fsCount: number): void {
    const findIndex = this.slots().prizes.findIndex(item => item?.fs === fsCount);
    this.patchStore({
      slots: {
        ...this.slots(),
        finalPrize: this.slots().prizes[findIndex] ?? this.slots().prizes[0] ?? null,
      },
    });
  }

  async checkActivatedBonuses() {
    if (this.store().isWelcomeModalOpened) {
      return of(true);
    }

    if (!this.store().isUserAlreadyActivatedPromoLootbox && this.store().isLoadedPromo && !this.store().page.isDisabledPromo) {
      this.patchStore({ isWelcomeModalOpened: true });
      const component = await import('../halloween/halloween-welcome-modal/halloween-welcome-modal.component');
      await this.modals.openLazy(component?.HalloweenWelcomeModalComponent);
      const now = new Date();
      const day = now.getDay();
      const diff = 7 - day;
      const endOfWeek = new Date(now);
      endOfWeek.setDate(now.getDate() + diff);
      endOfWeek.setHours(23, 59, 59, 999);
      this.cookie.set('welcome-modal-promo', 'true', endOfWeek, '/');
    }
    return of(true);
  }

  async openHuntModal(type: EventType) {
    const component = await import('../halloween/halloween-hunt-modal/halloween-hunt-modal.component');
    await this.modals.openLazy(component?.HalloweenHuntModalComponent, {
      template: 'CLEAR',
      data: {
        size: this.eventList.length,
        data: this.huntModalData,
      },
    });
  }
}
